import React, { useState, useMemo, useEffect } from "react";
import Select from "react-select";
import Button from "../../../UI/Buttons/Button/Button";
import NotificationMessage from "../../../UI/NotificationMessage/NotificationMessage";
import RegisterLicenseModal from "./RegisterLicenseModal/RegisterLicenseModal";
import DeleteConfirmationModal from "./DeleteConfirmationModal/DeleteConfirmationModal";
import { usePharmacyLicenses } from "../../../../hooks/usePharmacyLicenses";
import { formatLicenseLabel } from "../../../../services/pharmacyLicenseService";
import {
  PharmacyLicenseOption,
  PharmacyLicenseResponse,
} from "../../../../types/pharmacyLicense";
import editIcon from "../../../../assets/icons/edit.svg";
import deleteIcon from "../../../../assets/icons/trash.svg";
import styles from "./PharmacistLicenseSelector.module.scss";

const PharmacistLicenseSelector = () => {
  const {
    licenses,
    loading,
    error,
    activeLicenseId,
    create,
    update,
    remove,
    setActive,
  } = usePharmacyLicenses();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLicense, setEditingLicense] =
    useState<PharmacyLicenseResponse | null>(null);
  const [deletingLicense, setDeletingLicense] =
    useState<PharmacyLicenseResponse | null>(null);
  const [notificationVisible, setNotificationVisible] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");

  // Show error notification when hook error changes
  useEffect(() => {
    if (error) {
      showNotification(error);
    }
  }, [error]);

  const showNotification = (message: string) => {
    setNotificationMessage(message);
    setNotificationVisible(true);
    setTimeout(() => {
      setNotificationVisible(false);
    }, 5000);
  };

  // Prepare dropdown options
  const options: PharmacyLicenseOption[] = useMemo(() => {
    const noneOption: PharmacyLicenseOption = {
      value: null,
      label: "Izaberi farmaceuta",
    };

    const licenseOptions: PharmacyLicenseOption[] = licenses.map((license) => ({
      value: license.id,
      label: formatLicenseLabel(license),
    }));

    return [noneOption, ...licenseOptions];
  }, [licenses]);

  // Find currently selected option
  const selectedOption = useMemo(() => {
    return options.find((opt) => opt.value === activeLicenseId) || options[0];
  }, [options, activeLicenseId]);

  const handleLicenseChange = async (option: PharmacyLicenseOption | null) => {
    if (!option) return;

    const success = await setActive(option.value);
    if (success) {
      const message = option.value
        ? `Aktiviran farmaceut: ${option.label}`
        : "Farmaceut nije izabran";
      showNotification(message);
    }
  };

  const handleCreateLicense = async (
    data: any,
    files: File[]
  ): Promise<boolean> => {
    const success = await create(data, files);
    if (success) {
      showNotification("Licenca uspešno kreirana!");
    }
    return success;
  };

  const handleUpdateLicense = async (
    data: any,
    files: File[]
  ): Promise<boolean> => {
    const success = await update(data, files);
    if (success) {
      showNotification("Licenca uspešno izmenjena!");
    }
    return success;
  };

  const handleEditClick = () => {
    // Find the currently selected license
    const selectedLicense = licenses.find((l) => l.id === activeLicenseId);
    if (selectedLicense) {
      setEditingLicense(selectedLicense);
      setIsModalOpen(true);
    }
  };

  const handleDeleteClick = () => {
    // Find the currently selected license
    const selectedLicense = licenses.find((l) => l.id === activeLicenseId);
    if (selectedLicense) {
      setDeletingLicense(selectedLicense);
    }
  };

  const confirmDelete = async () => {
    if (!deletingLicense) return;

    const success = await remove(deletingLicense.id);
    if (success) {
      showNotification("Licenca uspešno obrisana!");
      setDeletingLicense(null);
    }
  };

  const cancelDelete = () => {
    setDeletingLicense(null);
  };

  if (loading && licenses.length === 0) {
    return (
      <div className={styles.PharmacistLicenseSection}>
        <div className={styles.LoadingText}>Učitavanje...</div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.PharmacistLicenseSection}>
        <div className={styles.Label}>Dežurni farmaceut</div>
        <div className={styles.SelectorWrapper}>
          <Select
            options={options}
            value={selectedOption}
            onChange={handleLicenseChange}
            className={styles.Select}
            classNamePrefix="react-select"
            isSearchable={false}
            isDisabled={loading}
            placeholder="Izaberi farmaceuta"
            menuPlacement="bottom"
            blurInputOnSelect={true}
            closeMenuOnSelect={true}
          />
          <div className={styles.ActionButtons}>
            <Button
              buttonType="icon"
              onClick={handleEditClick}
              disabled={!activeLicenseId || loading}
              title="Izmeni farmaceuta"
              aria-label="Izmeni farmaceuta"
            >
              <img src={editIcon} alt="Izmeni" />
            </Button>
            <Button
              buttonType="icon"
              onClick={handleDeleteClick}
              disabled={!activeLicenseId || loading}
              title="Obriši farmaceuta"
              aria-label="Obriši farmaceuta"
            >
              <img src={deleteIcon} alt="Obriši" />
            </Button>
          </div>
        </div>
        <Button
          className={styles.RegisterButton}
          onClick={() => setIsModalOpen(true)}
          buttonType="blue"
          disabled={loading}
        >
          Registruj novog farmaceuta
        </Button>
      </div>

      <RegisterLicenseModal
        show={isModalOpen}
        closeModal={() => {
          setIsModalOpen(false);
          setEditingLicense(null);
        }}
        onCreate={handleCreateLicense}
        onUpdate={handleUpdateLicense}
        initialData={editingLicense}
      />

      <DeleteConfirmationModal
        show={!!deletingLicense}
        license={deletingLicense}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        loading={loading}
      />

      {notificationVisible && (
        <NotificationMessage onClick={() => setNotificationVisible(false)}>
          {notificationMessage}
        </NotificationMessage>
      )}
    </>
  );
};

export default PharmacistLicenseSelector;
